import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import AddIcon from '@mui/icons-material/Add';
import {
    collection,
    onSnapshot,
    orderBy,
    query,
    limit,
} from 'firebase/firestore';
import { useDataProvider, useGetList, useNotify } from 'react-admin';
import { db } from '../../providers/firebase/firebase';
import type {
    GateAccountOption,
    GateMode,
    UnregisteredGateAccount,
} from '../../types/gates';

interface OperatorDoc {
    uid: string;
    account_ids: string[];
}

interface AuditEntry {
    id: string;
    at?: { toDate(): Date };
    account_id: string | null;
    condition_id: string | null;
    market_slug: string | null;
    actor: string;
    kind: 'decision' | 'request' | 'mode';
    action: string | null;
    approved_limit_usd: number | null;
    request_generation: number | null;
    // 'mode' entries only (functions/gateAudit.js buildGateModeAuditEntry):
    // before is null on the doc's first (create) write.
    before?: { mode: GateMode | null } | null;
    after?: { mode: GateMode | null };
}

function fmtAt(at?: { toDate(): Date }): string {
    if (!at) return '—';
    const d = at.toDate();
    return `${d.toLocaleDateString('de-CH')} ${d.toLocaleTimeString('de-CH')}`;
}

/**
 * Operators panel of /gates (admin viewers only) — who may clear gates for
 * which polymarket account. Assignments write ONLY through the audited
 * adminSetGateOperators callable. Assignment is the sole authority for
 * clearing gates and toggling modes — admins may be assigned like any other
 * user, including assigning themselves; an unassigned admin stays
 * read-only. Rendered as one tab of GatesView; no page-level chrome of its
 * own.
 */
export function GateOperatorsPanel() {
    const dataProvider = useDataProvider();
    const notify = useNotify();

    // All users (for names + the assign dialog target list).
    const { data: users } = useGetList('users', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'email', order: 'ASC' },
    });

    // Live assignment map (admin-readable per rules).
    const [operators, setOperators] = useState<OperatorDoc[]>([]);
    useEffect(() => {
        return onSnapshot(collection(db, 'gate_operators'), snap => {
            setOperators(
                snap.docs.map(d => ({
                    uid: d.id,
                    account_ids: (d.data().account_ids as string[]) ?? [],
                }))
            );
        });
    }, []);

    // Recent gate activity (decisions + bogg transitions).
    const [audit, setAudit] = useState<AuditEntry[]>([]);
    useEffect(() => {
        const q = query(
            collection(db, 'gate_audit'),
            orderBy('at', 'desc'),
            limit(50)
        );
        return onSnapshot(q, snap => {
            setAudit(
                snap.docs.map(d => ({
                    id: d.id,
                    ...(d.data() as Omit<AuditEntry, 'id'>),
                }))
            );
        });
    }, []);

    // Account registry (polymarket-only) — fetched on mount, refreshed
    // after a manual add. `address` (bare) is what gate_operators stores
    // and what assignment chips elsewhere display; `id` (registry-prefixed
    // `polymarket:0x...`) is storage-only, never shown or assigned.
    // `unregistered` (#117): accounts bogg writes gate requests for that
    // nobody registered yet — assignable directly (the callable
    // auto-registers on assign).
    const [allAccounts, setAllAccounts] = useState<GateAccountOption[]>([]);
    const [unregistered, setUnregistered] = useState<
        UnregisteredGateAccount[]
    >([]);

    const loadAccounts = useCallback(async () => {
        try {
            const res = await dataProvider.adminListAccounts();
            setAllAccounts(res?.accounts ?? []);
            setUnregistered(res?.unregistered ?? []);
        } catch {
            notify('Could not load account list', { type: 'warning' });
            setAllAccounts([]);
            setUnregistered([]);
        }
    }, [dataProvider, notify]);

    useEffect(() => {
        loadAccounts();
    }, [loadAccounts]);

    const nameByAddress = useMemo(
        () => new Map(allAccounts.map(a => [a.address, a.name])),
        [allAccounts]
    );

    // Assign dialog state.
    const [editUid, setEditUid] = useState<string | null>(null);
    const [draftIds, setDraftIds] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);

    // Add-account dialog state.
    const [addAccountOpen, setAddAccountOpen] = useState(false);
    const [addAccountId, setAddAccountId] = useState('');
    const [addAccountName, setAddAccountName] = useState('');
    const [addingAccount, setAddingAccount] = useState(false);

    // Assign-unregistered dialog state (#117): pick a user for one account.
    const [assignAddress, setAssignAddress] = useState<string | null>(null);
    const [assignUid, setAssignUid] = useState<string | null>(null);
    const [assigning, setAssigning] = useState(false);

    const emailByUid = useMemo(() => {
        const m = new Map<string, string>();
        for (const u of users ?? [])
            m.set(
                String(u.id),
                (u as { email?: string }).email ?? String(u.id)
            );
        return m;
    }, [users]);

    const openDialog = (uid: string, current: string[]) => {
        setEditUid(uid);
        setDraftIds(current);
    };

    // Closing without saving must drop any prefill (e.g. from the
    // unregistered panel's Add button) so the next manual add starts clean.
    const closeAddDialog = () => {
        setAddAccountOpen(false);
        setAddAccountId('');
        setAddAccountName('');
    };

    const saveAccount = async () => {
        setAddingAccount(true);
        try {
            await dataProvider.adminAddAccount(
                addAccountId,
                addAccountName.trim() || undefined
            );
            notify('Account added', { type: 'info' });
            setAddAccountOpen(false);
            setAddAccountId('');
            setAddAccountName('');
            await loadAccounts();
        } catch (e) {
            const msg = (e as Error).message ?? '';
            const friendly = msg.includes('account_exists')
                ? 'That account is already registered.'
                : msg.includes('invalid_polymarket_account')
                  ? 'Enter a non-empty account id (no "/" characters).'
                  : `Add account failed: ${msg}`;
            notify(friendly, { type: 'error' });
        } finally {
            setAddingAccount(false);
        }
    };

    const save = async () => {
        if (!editUid) return;
        setSaving(true);
        try {
            await dataProvider.adminSetGateOperators(editUid, draftIds);
            notify('Gate operator assignment saved', { type: 'info' });
            setEditUid(null);
        } catch (e) {
            notify(`Assignment failed: ${(e as Error).message}`, {
                type: 'error',
            });
        } finally {
            setSaving(false);
        }
    };

    // All users are eligible as operators — assignment is the sole
    // authority for gate powers, and that now includes admins (incl.
    // self-assignment).
    const operatorByUid = new Map(operators.map(o => [o.uid, o]));

    // Assign an unregistered account to the picked user: the callable
    // auto-registers it (audited) and appends it to the user's existing
    // assignment set.
    const saveAssign = async () => {
        if (!assignAddress || !assignUid) return;
        setAssigning(true);
        try {
            const current = operatorByUid.get(assignUid)?.account_ids ?? [];
            await dataProvider.adminSetGateOperators(assignUid, [
                ...current.filter(id => id !== assignAddress),
                assignAddress,
            ]);
            notify('Account registered and assigned', { type: 'info' });
            setAssignAddress(null);
            setAssignUid(null);
            await loadAccounts();
        } catch (e) {
            notify(`Assignment failed: ${(e as Error).message}`, {
                type: 'error',
            });
        } finally {
            setAssigning(false);
        }
    };

    return (
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 2,
                    }}
                >
                    <Box>
                        <Typography
                            variant="h6"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 1,
                            }}
                        >
                            <GavelIcon fontSize="small" /> Gate operators
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Who may clear exposure gates for which polymarket
                            account. Assignment is the sole authority — an
                            assigned admin clears gates like any other operator;
                            an unassigned admin stays read-only. Every change is
                            recorded in the admin audit log.
                        </Typography>
                    </Box>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon fontSize="small" />}
                        onClick={() => setAddAccountOpen(true)}
                        sx={{ flexShrink: 0 }}
                    >
                        Add account
                    </Button>
                </Box>
                <Table size="small" sx={{ mt: 1 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>User</TableCell>
                            <TableCell>Assigned accounts</TableCell>
                            <TableCell align="right" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(users ?? []).map(u => {
                            const uid = String(u.id);
                            const assigned =
                                operatorByUid.get(uid)?.account_ids ?? [];
                            return (
                                <TableRow key={uid}>
                                    <TableCell>{emailByUid.get(uid)}</TableCell>
                                    <TableCell>
                                        {assigned.length === 0 ? (
                                            <Typography
                                                variant="caption"
                                                color="text.disabled"
                                            >
                                                none
                                            </Typography>
                                        ) : (
                                            assigned.map(id => (
                                                <Chip
                                                    key={id}
                                                    label={id}
                                                    size="small"
                                                    sx={{
                                                        mr: 0.5,
                                                        fontFamily: 'monospace',
                                                        fontSize: 11,
                                                    }}
                                                />
                                            ))
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Button
                                            size="small"
                                            onClick={() =>
                                                openDialog(uid, assigned)
                                            }
                                        >
                                            Edit
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </Box>

            {unregistered.length > 0 && (
                <Box>
                    <Typography variant="h6" sx={{ mb: 0.5 }}>
                        Unregistered accounts with gate requests
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        bogg is writing gate requests for these accounts, but
                        they are not in the registry yet — nobody can clear
                        their gates. Add registers without assigning; Assign
                        registers and grants in one step (both audited).
                    </Typography>
                    <Table size="small" sx={{ mt: 1 }}>
                        <TableBody>
                            {unregistered.map(u => (
                                <TableRow key={u.address}>
                                    <TableCell
                                        sx={{
                                            fontFamily: 'monospace',
                                            fontSize: 12,
                                        }}
                                    >
                                        {u.address}
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="caption"
                                            color="warning.main"
                                        >
                                            {u.pending_requests} pending
                                            request
                                            {u.pending_requests === 1
                                                ? ''
                                                : 's'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Button
                                            size="small"
                                            onClick={() => {
                                                setAddAccountId(u.address);
                                                setAddAccountOpen(true);
                                            }}
                                        >
                                            Add
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() =>
                                                setAssignAddress(u.address)
                                            }
                                        >
                                            Assign…
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            )}

            <Box>
                <Typography variant="h6" sx={{ mb: 1 }}>
                    Recent gate activity
                </Typography>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>At</TableCell>
                            <TableCell>Actor</TableCell>
                            <TableCell>Kind</TableCell>
                            <TableCell>Account</TableCell>
                            <TableCell>Market</TableCell>
                            <TableCell>Detail</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {audit.map(e => (
                            <TableRow key={e.id}>
                                <TableCell
                                    sx={{
                                        whiteSpace: 'nowrap',
                                        fontFamily: 'monospace',
                                        fontSize: 11,
                                    }}
                                >
                                    {fmtAt(e.at)}
                                </TableCell>
                                <TableCell>
                                    {e.actor === 'bogg'
                                        ? 'bogg'
                                        : emailByUid.get(e.actor) ?? e.actor}
                                </TableCell>
                                <TableCell>{e.kind}</TableCell>
                                <TableCell
                                    sx={{
                                        fontFamily: 'monospace',
                                        fontSize: 11,
                                    }}
                                >
                                    {e.account_id}
                                </TableCell>
                                <TableCell sx={{ fontSize: 12 }}>
                                    {e.market_slug ?? e.condition_id}
                                </TableCell>
                                <TableCell sx={{ fontSize: 12 }}>
                                    {e.kind === 'decision'
                                        ? `${e.action}${
                                              e.action === 'approve'
                                                  ? ` · ${e.approved_limit_usd != null ? `$${e.approved_limit_usd}` : 'unbounded'}`
                                                  : ''
                                          }`
                                        : e.kind === 'mode'
                                          ? e.before
                                              ? `mode: ${e.before.mode ?? '—'} → ${e.after?.mode ?? '—'}`
                                              : `mode set → ${e.after?.mode ?? '—'}`
                                          : `requested (gen ${e.request_generation ?? '?'})`}
                                </TableCell>
                            </TableRow>
                        ))}
                        {audit.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6}>
                                    <Typography
                                        variant="caption"
                                        color="text.disabled"
                                    >
                                        No gate activity yet
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Box>

            <Dialog
                open={editUid !== null}
                onClose={() => setEditUid(null)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    Assign accounts — {editUid ? emailByUid.get(editUid) : ''}
                </DialogTitle>
                <DialogContent>
                    <Autocomplete
                        multiple
                        options={[
                            ...allAccounts.map(a => a.address),
                            ...unregistered.map(u => u.address),
                        ]}
                        value={draftIds}
                        onChange={(_, v) => setDraftIds(v)}
                        renderOption={(props, option) => (
                            <li {...props}>
                                <Box>
                                    <Typography
                                        sx={{
                                            fontFamily: 'monospace',
                                            fontSize: 12,
                                        }}
                                    >
                                        {option}
                                    </Typography>
                                    {nameByAddress.get(option) ? (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            {nameByAddress.get(option)}
                                        </Typography>
                                    ) : unregistered.some(
                                          u => u.address === option
                                      ) ? (
                                        <Typography
                                            variant="caption"
                                            color="warning.main"
                                        >
                                            not registered yet — registers on
                                            assign
                                        </Typography>
                                    ) : null}
                                </Box>
                            </li>
                        )}
                        renderInput={params => (
                            <TextField
                                {...params}
                                label="Polymarket accounts"
                                margin="dense"
                                helperText="On-chain polygon account ids this user may clear gates for"
                            />
                        )}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    {...getTagProps({ index })}
                                    key={option}
                                    label={option}
                                    size="small"
                                    sx={{
                                        fontFamily: 'monospace',
                                        fontSize: 11,
                                    }}
                                />
                            ))
                        }
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditUid(null)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={save}
                        disabled={saving}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={assignAddress !== null}
                onClose={() => {
                    setAssignAddress(null);
                    setAssignUid(null);
                }}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    Assign {assignAddress} to a user
                </DialogTitle>
                <DialogContent>
                    <Autocomplete
                        options={(users ?? []).map(u => String(u.id))}
                        getOptionLabel={uid =>
                            emailByUid.get(uid) ?? uid
                        }
                        value={assignUid}
                        onChange={(_, v) => setAssignUid(v)}
                        renderInput={params => (
                            <TextField
                                {...params}
                                label="User"
                                margin="dense"
                                helperText="The account is registered automatically when you save"
                            />
                        )}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setAssignAddress(null);
                            setAssignUid(null);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={saveAssign}
                        disabled={assigning || !assignUid}
                    >
                        Register & assign
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={addAccountOpen}
                onClose={closeAddDialog}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Add polymarket account</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        margin="dense"
                        label="Polymarket account id"
                        helperText="Any account id — stored exactly as entered"
                        value={addAccountId}
                        onChange={e => setAddAccountId(e.target.value)}
                    />
                    <TextField
                        fullWidth
                        margin="dense"
                        label="Name (optional)"
                        value={addAccountName}
                        onChange={e => setAddAccountName(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeAddDialog}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={saveAccount}
                        disabled={addingAccount || !addAccountId.trim()}
                    >
                        Add
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
